class NSSContact extends HTMLElement {
  constructor() {
    super();

    this.debug = false;

    this.els = {
      phoneGroup: this.querySelector(`.nss-contact__phone-group`),
      formDialCodeButton: this.querySelector(`#ContactFormDialCodeButton`),
      spanFlag: this.querySelector(`.nss-phone-group-flag`),
      spanCode: this.querySelector(`.nss-phone-group-code`),
      formDialCodeSelect: this.querySelector(`#ContactFormDialCodeSelect`),
      formPhoneOnly: this.querySelector(`#ContactFormPhoneOnly`),
      formPhone: this.querySelector(`#ContactFormPhone`),
    };
  }

  connectedCallback() {
    if (this.debug) console.log(`[NSS][Contact] Connected.`);

    // The phone field is optional (`show_phone`). Every phone-related line below only runs when
    // the field is actually present, otherwise a null dereference here would throw and take the
    // inquiry listbox init below with it, since it's the same callback.
    if (this.els.phoneGroup && this.els.formPhone) {
      this.initFlagSelect();
      this.updateFormPhone();
      this.els.phoneGroup.style.display = `flex`;
      this.els.formPhone.setAttribute(`type`, `hidden`);

      this.els.formDialCodeSelect.addEventListener(`change`, this.updateFormPhone.bind(this));
      this.els.formPhoneOnly.addEventListener(`keyup`, this.updateFormPhone.bind(this));
    }

    this.initInquiry();
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    document.addEventListener(`click`, this.onDocumentClick);
    this.addEventListener(`keydown`, this.handleKeydown.bind(this));
  }

  disconnectedCallback() {
    document.removeEventListener(`click`, this.onDocumentClick);
  }

  // ---- Inquiry listbox -------------------------------------------------------------
  // A custom listbox rather than a native `<select>`, so the open state can be fully styled.
  // The value still leaves via a hidden input under the same name, so the Shopify contact form
  // is untouched.
  initInquiry() {
    this.inquiry = {
      root: this.querySelector(`[nss-inquiry]`),
      toggle: this.querySelector(`[nss-inquiry-toggle]`),
      label: this.querySelector(`[nss-inquiry-label]`),
      panel: this.querySelector(`[nss-inquiry-panel]`),
      input: this.querySelector(`[nss-inquiry-input]`),
    };
    if (!this.inquiry.root) return;

    this.placeholder = this.inquiry.label.textContent.trim();
    this.inquiry.toggle.addEventListener(`click`, () => this.toggleInquiry());
    this.inquiry.panel.addEventListener(`click`, (e) => {
      const optionEl = e.target.closest(`[nss-inquiry-option]`);
      if (optionEl) this.selectInquiry(optionEl);
    });

    // The listbox's value leaves through a hidden input, and `required` on a hidden input is
    // ignored by constraint validation entirely — the browser won't block a submit on it or
    // report it. Guarded here instead, since this is the one field the browser can't police:
    // it's the one field that isn't a real control, which is why it's custom in the first place.
    this.form = this.querySelector(`form`);
    if (this.form) this.form.addEventListener(`submit`, this.handleSubmit.bind(this));

    this.updateInquiryFromURL();
  }

  handleSubmit(e) {
    if (!this.inquiry || !this.inquiry.root || this.inquiry.input.value) return;

    e.preventDefault();
    this.setInquiryInvalid(true);
    // Focus the toggle rather than the hidden input, which can't take focus. Scrolled into view
    // because the listbox can sit above the fold on a long form, and a blocked submit with no
    // visible reason reads as a broken button.
    this.inquiry.toggle.focus();
    this.inquiry.root.scrollIntoView({ block: `center`, behavior: `smooth` });
  }

  setInquiryInvalid(invalid) {
    if (!this.inquiry || !this.inquiry.root) return;
    this.inquiry.root.toggleAttribute(`nss-inquiry-invalid`, invalid);
    this.inquiry.toggle.setAttribute(`aria-invalid`, `${invalid}`);
  }

  toggleInquiry(open) {
    const { toggle, panel } = this.inquiry;
    const next = open === undefined ? panel.hidden : open;
    panel.hidden = !next;
    toggle.setAttribute(`aria-expanded`, `${next}`);
  }

  selectInquiry(optionEl, silent) {
    const value = optionEl.getAttribute(`nss-inquiry-value`);
    this.inquiry.input.value = value;
    this.inquiry.label.textContent = value;
    this.inquiry.root.setAttribute(`nss-inquiry-chosen`, ``);
    // Clears the moment the reason for it is gone, the way a native control does.
    this.setInquiryInvalid(false);

    this.querySelectorAll(`[nss-inquiry-option]`).forEach((el) => {
      el.setAttribute(`aria-selected`, `${el === optionEl}`);
    });

    this.toggleInquiry(false);
    if (!silent) this.updateInquiryURL(optionEl);
  }

  handleDocumentClick(e) {
    if (!this.inquiry || !this.inquiry.root) return;
    if (!this.inquiry.root.contains(e.target)) this.toggleInquiry(false);
  }

  handleKeydown(e) {
    if (e.key === `Escape` && this.inquiry && this.inquiry.root) this.toggleInquiry(false);
  }

  updateInquiryURL(optionEl) {
    const id = optionEl.getAttribute(`nss-inquiry-id`);
    if (!id) return;
    const url = new URL(window.location);
    url.searchParams.set(`inquiry`, id);
    history.pushState(null, ``, url);
  }

  // `?inquiry=<id>` deep-links a pre-selected type. Silent, so landing on the link doesn't
  // immediately push the same URL back into history.
  updateInquiryFromURL() {
    const id = new URL(window.location).searchParams.get(`inquiry`);
    if (!id) return;
    const optionEl = this.querySelector(`[nss-inquiry-id="${id}"]`);
    if (optionEl) this.selectInquiry(optionEl, true);
    else if (this.debug) console.warn(`[NSS][Contact] No option matching inquiry id ${id}!`);
  }

  updateFormPhone() {
    const code = this.els.formDialCodeSelect.value;
    const entry = this.els.formPhoneOnly.value;
    this.els.formPhone.value = `${code} ${entry}`;
    this.updateDialCodeButton();
    this.els.formDialCodeSelect.style.width = `${this.els.formDialCodeButton.clientWidth}px`;
  }

  updateDialCodeButton() {
    const optionEl = this.els.formDialCodeSelect.options[this.els.formDialCodeSelect.selectedIndex];
    if (optionEl) {
      const flagEmoji = this.getFlagEmoji(optionEl.getAttribute(`nss-country-code`));
      this.els.spanFlag.innerHTML = flagEmoji;
    }

    this.els.spanCode.innerHTML = this.els.formDialCodeSelect.value;
  }

  initFlagSelect() {
    fetch(this.els.formDialCodeSelect.getAttribute(`nss-data-url`))
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        data.forEach((country, index) => {
          country.callingCodes.forEach((callingCode) => {
            this.createCountryOption(country, callingCode, index);
          });
        });
      });
  }

  createCountryOption(country, callingCode, index) {
    const flagEmoji = this.getFlagEmoji(country.alpha2Code);
    const optionEl = document.createElement(`option`);
    const code = `${callingCode ? `+${callingCode}` : ""}`;
    optionEl.innerHTML = `${flagEmoji} ${country.name} ${code}`;
    optionEl.value = `${code}`;
    optionEl.setAttribute(`nss-country-code`, country.alpha2Code);
    this.els.formDialCodeSelect.appendChild(optionEl);
    if (country.alpha2Code === this.els.formDialCodeSelect.getAttribute(`nss-current-country`)) {
      // this.els.formDialCodeSelect.value = code; // There are duplicated values
      this.els.formDialCodeSelect.options.selectedIndex = index + 1; // Not sure why we're off by one here
    }
    this.els.formDialCodeSelect.dispatchEvent(new Event("change"));
  }

  // https://dev.to/jorik/country-code-to-flag-emoji-a21
  getFlagEmoji(countryCode) {
    const codePoints = countryCode
      .toUpperCase()
      .split("")
      .map((char) => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  }
}

let elementName = `nss-contact`;
if (typeof customElements.get(elementName) === `undefined`) {
  customElements.define(elementName, NSSContact);
} else {
  console.warn(`[NSS] Element '${elementName}' already defined.`);
}
